import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Sector } from 'recharts';
import { AlertCircle, Loader2 } from 'lucide-react';
import DashboardTabs from '../../../components/dashboard/tabs/DashboardTabs';
import Loader from '../../../components/Loader';
import { sentenceCase } from '../../../utils/miscFunctions';

export interface IMonthlyTrendItem {
  month: string;
  year: number;
  total: number;
  completed: number;
}
interface VehicleVerificationMonthlyTrendsChartProps {
  monthlyTrend: IMonthlyTrendItem[];
  protectionTypeDistribution: Record<string, number>;
  statusDistribution: Record<string, number>;
  title?: string;
  description?: string;
  isLoading?: boolean;
  error?: string | null;
}

const COLORS = ['#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#0088FE'];

interface ActiveShapeProps {
  cx?: number;
  cy?: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}

const ActiveShape = (props: ActiveShapeProps) => {
  const { cx = 0, cy = 0, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  );
};


const CustomTooltip = ({ active, payload, name }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  return (
    <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md text-sm">
      <p className="font-medium text-gray-900 dark:text-darkBg-mainText">{sentenceCase(data.name)}</p>
      <p className="text-gray-600">Count: {data.value}</p>
      {data.percentage && <p className="text-gray-600">Percentage: {(data.percentage * 100).toFixed(1)}%</p>}
    </div>
  );
};

const VehicleVerificationMonthlyTrendsChart = ({
  monthlyTrend,
  protectionTypeDistribution = {},
  statusDistribution = {},
  title = 'Verification Overview',
  description = 'Monthly trends and distribution data',
  isLoading = false,
  error = null,
}: VehicleVerificationMonthlyTrendsChartProps) => {



  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 p-6 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center space-y-4">
          <Loader message="Loading chart data..." />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 p-6 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center space-y-4 text-center p-6">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Unable to load data</h3>
          <p className="text-sm text-gray-500 max-w-md">
            {error || 'An error occurred while loading the chart data. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (monthlyTrend.length === 0) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 p-6 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center space-y-4 text-center p-6">
          <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center dark:bg-darkBg dark:border dark:border-darkBg-border">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">No data available</h3>
          <p className="text-sm text-gray-500 max-w-md">
            There is no data to display for the selected time period.
          </p>
        </div>
      </div>
    );
  }

  // Prepare data for pie charts
  const protectionTypeData = Object.entries(protectionTypeDistribution).map(([name, value], index) => ({
    name: name.split('_').join(' '),
    value,
    color: COLORS[index % COLORS.length]
  }));

  const statusData = Object.entries(statusDistribution).map(([name, value], index) => ({
    name,
    value,
    color: COLORS[index % COLORS.length]
  }));

  const renderPieChart = (data: Array<{ name: string; value: number; color?: string }>, name: string) => {
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const onPieEnter = (_: any, index: number) => {
      setActiveIndex(index);
    };

    if (!data.length) {
      return <div className="h-full flex items-center justify-center text-gray-500">No data available</div>;
    }

    // Calculate total for percentage
    const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
    const dataWithPercentage = data.map(item => ({
      ...item,
      percentage: total > 0 ? (item.value || 0) / total : 0
    }));

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                //activeIndex={activeIndex}
                activeShape={ActiveShape as any}
                data={dataWithPercentage}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                labelLine={false}
                onMouseEnter={onPieEnter}
                onMouseLeave={() => setActiveIndex(-1)}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip name={sentenceCase(name)} />} />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                formatter={(value: string) => <span className="text-xs text-gray-700">{sentenceCase(value)}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow-sm border border-gray-200 p-6 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Trend Bar Chart */}
        <div className="lg:col-span-2 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={monthlyTrend}
              margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12 }}
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value: number, name: string) => [value, name]}
                labelFormatter={(label) => sentenceCase(label)}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '4px',
                  padding: '10px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}
              />
              <Bar
                dataKey="total"
                name="Total Verifications"
                fill="#FFBB28"
                radius={[4, 4, 0, 0]}
                barSize={30}
                fillOpacity={0.8}
              />
              <Bar
                dataKey="completed"
                name="Completed"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
                barSize={30}
                fillOpacity={0.8}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => <span className="text-sm text-gray-700">{sentenceCase(value)}</span>}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-1 h-60">
          <DashboardTabs
            tabGroupTitle="Products"
            titleClassName="text-lg font-medium text-gray-900 dark:text-darkBg-mainText"
            tab1={{
              name: 'Protection Type',
              children: (
                <ResponsiveContainer width="100%" height="100%">
                  {renderPieChart(protectionTypeData, 'Protection Type Distribution')}
                </ResponsiveContainer>
              )
            }}
            tab2={{
              name: 'Status',
              children: (
                <ResponsiveContainer width="100%" height="100%">
                  {renderPieChart(statusData, 'Status Distribution')}
                </ResponsiveContainer>
              )
            }}
            activeTab={'Status'}
          />
        </div>
      </div>
    </div>
  );
};

export default VehicleVerificationMonthlyTrendsChart;
